/**
 * StagePaymentGate — "INSERT COIN": prove the wallet, pay $1 in SOL / USDT /
 * $WOOD for one arcade credit (3 lives), then `onPaid()` spends it (a new
 * game or a continue). The server only spends verified payments, so there
 * is no client-side skip.
 */

import SoundManager from "../SoundManager";
import PaymentManager from "../PaymentManager";
import WalletManager from "../WalletManager";
import { FONT_SIZE, fontStyle } from "../FontScale";
import { toHex } from "../view/colors";
import { fitText } from "../view/layout";

const DEFAULT_TOP = 330;
const DEPTH = 410; // above the stage result panel

/** The gate starts below whatever the stage-clear overlay drew. */
const gateTop = (scene) => scene.overlayNextY ?? DEFAULT_TOP;

/**
 * @param {Phaser.Scene} scene - uses scene.overlayNextY (below the result panel)
 * @param {{ stage: number, handle: string, subtitle: string, onPaid: () => void }} p
 *   stage: where the coin goes in (for the prize split); onPaid spends the credit
 */
export function showPaymentGate(scene, { stage, handle, subtitle, onPaid }) {
  const nextLevel = stage;
  scene.onPaid = onPaid;
  const statusText = scene.add
    .text(scene._CX, gateTop(scene) + 130, "", fontStyle(FONT_SIZE.CAPTION, "#FFFFFF", { stroke: "#000000", strokeThickness: 3 }))
    .setOrigin(0.5, 0)
    .setDepth(DEPTH);

  const CX = scene._CX;
  const top = gateTop(scene);
  scene.add.text(CX, top, "INSERT COIN  -  $1 = 3 LIVES", fontStyle(FONT_SIZE.SUBHEADING, "#FFD700")).setOrigin(0.5, 0).setDepth(DEPTH);
  fitText(
    scene.add.text(CX, top + 18, subtitle, fontStyle(FONT_SIZE.CAPTION, "#AAAAAA")).setOrigin(0.5, 0).setDepth(DEPTH),
    scene._W - 24,
  );

  if (WalletManager.isConnected() && WalletManager.isLinkedTo(handle)) {
    showPaymentOptions(scene, nextLevel, statusText);
  } else {
    showConnectButton(scene, nextLevel, handle, statusText);
  }
}

function showConnectButton(scene, nextLevel, handle, statusText) {
  const CX = scene._CX;
  const top = gateTop(scene);
  const btn = scene.add
    .text(CX, top + 40, "CONNECT WALLET", fontStyle(FONT_SIZE.SUBHEADING, "#B580FF"))
    .setOrigin(0.5, 0)
    .setInteractive({ useHandCursor: true })
    .setDepth(DEPTH);

  btn.on("pointerdown", async () => {
    btn.setText("CONNECTING...");
    try {
      if (!WalletManager.isConnected()) await WalletManager.connect();
      // Payments are only credited to a wallet proven to be the player's
      btn.setText("SIGN IN WALLET...");
      const link = await WalletManager.linkToAccount(handle);
      if (!link.ok) {
        btn.setText("CONNECT WALLET");
        if (statusText) statusText.setText(link.error.toUpperCase());
        return;
      }
      btn.destroy();
      showPaymentOptions(scene, nextLevel, statusText);
    } catch {
      btn.setText("CONNECT WALLET");
      if (statusText) statusText.setText("WALLET NOT FOUND");
      if (!WalletManager.isPhantomInstalled()) {
        scene.add
          .text(CX, top + 64, "GET PHANTOM WALLET", fontStyle(FONT_SIZE.CAPTION, "#B580FF"))
          .setOrigin(0.5, 0)
          .setInteractive({ useHandCursor: true })
          .setDepth(DEPTH)
          .on("pointerdown", () => window.open(WalletManager.getPhantomInstallUrl(), "_blank"));
      }
    }
  });
}

function payButton(scene, x, y, w, h, label, color) {
  const bg = scene.add.graphics().setDepth(DEPTH);
  bg.fillStyle(color, 0.15);
  bg.fillRoundedRect(x, y, w, h, 6);
  bg.lineStyle(2, color, 0.8);
  bg.strokeRoundedRect(x, y, w, h, 6);
  return fitText(
    scene.add
      .text(x + w / 2, y + h / 2, label, fontStyle(FONT_SIZE.BODY, toHex(color)))
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1),
    w - 12,
  );
}

function showPaymentOptions(scene, nextLevel, statusText) {
  const CX = scene._CX;
  const top = gateTop(scene);
  scene.add
    .text(CX, top + 38, `WALLET: ${WalletManager.getShortKey()}`, fontStyle(FONT_SIZE.CAPTION, "#B580FF"))
    .setOrigin(0.5, 0)
    .setDepth(DEPTH);

  const gap = 16;
  const btnW = Math.min(180, (scene._W - 32 - gap) / 2);
  const btnH = 32;
  const startX = CX - btnW - gap / 2;
  const optY = top + 56;
  const pay = (token) => () => processPayment(scene, token, nextLevel, statusText);

  payButton(scene, startX, optY, btnW, btnH, "PAY SOL", 0x00ffff).on("pointerdown", pay("SOL"));
  payButton(scene, startX + btnW + gap, optY, btnW, btnH, "PAY USDT", 0x26a17b).on("pointerdown", pay("USDT"));
  if (process.env.REACT_APP_WOOD_MINT) {
    payButton(scene, CX - btnW / 2, optY + btnH + 10, btnW, btnH, "PAY $WOOD", 0xff00ff).on("pointerdown", pay("WOOD"));
  }
}

async function processPayment(scene, token, nextLevel, statusText) {
  if (scene.paymentInFlight) return;
  scene.paymentInFlight = true;
  const say = (text) => statusText && statusText.setText(text);
  say("PROCESSING...");
  try {
    const payers = {
      SOL: () => PaymentManager.payWithSOL(nextLevel),
      USDT: () => PaymentManager.payWithUSDT(nextLevel),
      WOOD: () => PaymentManager.payWithWOOD(nextLevel),
    };
    const paid = await payers[token]();
    if (!paid?.signature) return;

    say("VERIFYING...");
    const verified = await PaymentManager.verifyPayment(paid.signature, nextLevel, token);
    if (!verified?.verified) {
      say((verified?.error || "VERIFICATION FAILED - TRY AGAIN").substring(0, 40).toUpperCase());
      return;
    }

    SoundManager.playCoinDrop();
    say("COIN IN!");
    const coins = parseInt(localStorage.getItem("woodchuck_coins") || "0", 10);
    localStorage.setItem("woodchuck_coins", String(coins + 1));
    scene.time.delayedCall(800, () => scene.onPaid());
  } catch (err) {
    say((err.message || "Payment failed").substring(0, 30).toUpperCase());
  } finally {
    scene.paymentInFlight = false;
  }
}
